import { ITrackingResponse, TrackingEventState } from "@swyft/swyft-common";
import { format } from "~/helpers/date";
import { Box, styled, Typography } from "@helo/ui";
import { Theme } from "@helo/ui/theme";
import { statesDataMap } from "~/helpers/components/steps/step";

interface ExpectedDeliveryProps {
  date?: Date | string;
  currentStatus: ITrackingResponse["currentStatus"];
  currentAttempt: number;
  timezone?: string;
}

const StatusBox = styled(Box)({
  textAlign: "center",
  margin: 0,
  padding: "15px",
  borderRadius: "8px",
});

const getStateStyling = (theme: Theme, state: TrackingEventState): object => {
  switch (state) {
    case TrackingEventState.PENDING:
    case TrackingEventState.RECEIVED:
    case TrackingEventState.IN_TRANSIT:
    case TrackingEventState.DELIVERED:
      return {
        borderColor: theme.palette.success.light,
      };
    case TrackingEventState.DELAYED:
    case TrackingEventState.FAILED:
      return {
        borderColor: theme.palette.accentYellow.light,
      };
    case TrackingEventState.RETURNING_TO_SENDER:
    case TrackingEventState.PROBLEM:
    case TrackingEventState.CANCELED:
      return {
        borderColor: theme.palette.error.light,
      };
    default:
      return {
        borderColor: theme.palette.primaryScale[100],
      };
  }
};

const Message = ({ date, currentStatus, timezone }: ExpectedDeliveryProps) => {
  const {
    PENDING,
    RETURNING_TO_SENDER,
    FAILED,
    RECEIVED,
    DELIVERED,
    IN_TRANSIT,
    DELAYED,
    PROBLEM,
    CANCELED,
  } = TrackingEventState;

  if (!currentStatus || currentStatus.state === PENDING) {
    return (
      <>
        <Typography variant="h5" component="h3" fontWeight={600}>
          Awaiting Package
        </Typography>
      </>
    );
  }

  if (currentStatus.state === PROBLEM) {
    return (
      <>
        <Typography variant="h5" component="h3" fontWeight={600}>
          Problem in Delivery
        </Typography>
        <Typography variant="body2" component="p" sx={{ mt: 1 }}>
          Sorry, your package had an issue. Please contact customer support for
          further details.
        </Typography>
      </>
    );
  }

  if (currentStatus.state === CANCELED) {
    return (
      <>
        <Typography variant="h5" component="h3" fontWeight={600}>
          Canceled
        </Typography>
        <Typography variant="body2" component="p" sx={{ mt: 1 }}>
          Package canceled
        </Typography>
      </>
    );
  }

  if (currentStatus.state === RETURNING_TO_SENDER) {
    return (
      <>
        <Typography variant="h5" component="h3" fontWeight={600}>
          Returning to Sender
        </Typography>
        <Typography variant="body2" component="p" sx={{ mt: 1 }}>
          We were unable to deliver your package. The package will be returned.
        </Typography>
      </>
    );
  }

  if (currentStatus.state === IN_TRANSIT) {
    return (
      <>
        <Typography variant="h5" component="h3" fontWeight={600}>
          Out for Delivery
        </Typography>
      </>
    );
  }

  if (currentStatus.state === DELIVERED) {
    return (
      <Typography variant="h5" component="h3" fontWeight={600}>
        Delivered
      </Typography>
    );
  }

  if (currentStatus.state === DELAYED) {
    return (
      <>
        <Typography variant="h5" component="h3" fontWeight={600}>
          Delayed
        </Typography>
        <Typography variant="body2" component="p" sx={{ mt: 1 }}>
          {
            "Sorry, we had an issue with your delivery. We will reattempt your delivery within 2 business days."
          }
        </Typography>
      </>
    );
  }

  if (currentStatus.state === FAILED) {
    return (
      <>
        <Typography variant="h5" component="h3" fontWeight={600}>
          {statesDataMap["FAILED"].failures[currentStatus.failureCode]?.title ||
            "Delivery Failed"}
        </Typography>
        <Typography variant="body2" component="p" sx={{ mt: 1 }}>
          {statesDataMap["FAILED"].failures[currentStatus.failureCode]?.details ||
            "Sorry, we had an issue with your delivery. We will reattempt your delivery within 2 business days."}
        </Typography>
      </>
    );
  }

  if (currentStatus.state === RECEIVED) {
    return (
      <>
        <Typography variant="h5" component="h3" fontWeight={600}>
          Scanned at Warehouse
        </Typography>
      </>
    );
  }

  return (
    <>
      <Typography variant="h5" component="h3" fontWeight={600}>
        Expected Delivery
      </Typography>
      <Typography variant="body2" component="p" sx={{ mt: 1 }}>
        {format(date, timezone)}
      </Typography>
    </>
  );
};

const ExpectedDelivery = (props: ExpectedDeliveryProps) => {
  const {
    PENDING,
    RECEIVED,
    IN_TRANSIT,
    DELIVERED,
    FAILED,
    DELAYED,
    PROBLEM,
    RETURNING_TO_SENDER,
    CANCELED,
  } = TrackingEventState;

  const isSpecificStatus = [
    PENDING,
    RECEIVED,
    IN_TRANSIT,
    DELIVERED,
    FAILED,
    DELAYED,
    PROBLEM,
    RETURNING_TO_SENDER,
    CANCELED,
  ].includes(props.currentStatus?.state);

  if (!props.currentStatus || isSpecificStatus) {
    return (
      <StatusBox
        sx={(theme) => ({
          color: theme.palette.secondary.main,
          bgcolor: theme.palette.neutralScale[1000],
          borderBottom: "5px solid transparent",
          ...getStateStyling(theme, props.currentStatus?.state),
        })}
      >
        <Message {...props} />
      </StatusBox>
    );
  }

  return <></>;
};
export default ExpectedDelivery;
